<script setup>
// Memberships & passes → What you offer: the table of the club's types (X100,
// D39). One row per type: the kind's icon, the name (a link to the type) with
// its who · length · New to the club line, the price (and "N waiting"), the
// Membership / Pass chip beside the status chip, the current count, and the
// row's actions, which the page passes in its `actions` slot (edit, the status
// menu). On a phone the row folds into two lines. Pulled out of the admin
// page so /features/memberships can run the real table on demo data.
import MembershipStatusChip from '~/components/admin/memberships/MembershipStatusChip.vue';
import {COVERS} from '~/utils/membershipGroups';
import {DISPLAY_AS, displayAsLabel} from '~/utils/memberships';
import {typePhoneLine} from '~/utils/membershipsPage';

defineProps({
  types: {type: Array, default: () => []},
  clubId: {type: String, default: null},
  /** Type id → true while its row's action runs (the page's rowBusy). */
  busy: {type: Object, default: () => ({})},
});

const metaLine = (type) => [type.covers === COVERS.group ? type.whoItCovers : type.whoCanJoin, type.lengthSummary, type.newToClubOnly ? 'New to the club' : null,
  type.needsChildMembership ? "With a child's membership" : null].filter(Boolean).join(' · ');
</script>

<template>
  <div class="tl-table-wrap">
    <table class="tl-table ms-types-table">
      <thead>
        <tr>
          <th>Name</th>
          <th class="d-none d-sm-table-cell">Price</th>
          <th class="d-none d-sm-table-cell">Status</th>
          <th class="tl-num d-none d-sm-table-cell">Current</th>
          <th><span class="d-sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="type in types" :key="type.id">
          <td class="ms-type-cell">
            <div class="tl-cell-who">
              <div class="tl-cell-face" :class="type.displayAs === DISPLAY_AS.pass ? 'tl-cell-face--muted' : ''">
                <v-icon size="17">{{ type.displayAs === DISPLAY_AS.pass ? 'mdi-ticket-confirmation-outline' : 'mdi-card-account-details-outline' }}</v-icon>
              </div>
              <div class="ms-type-text">
                <nuxt-link v-if="clubId" class="tl-cell-title ms-type-name" :to="{name: 'ClubMembershipType', params: {clubId, typeId: type.id}}">{{ type.name }}</nuxt-link>
                <span v-else class="tl-cell-title ms-type-name">{{ type.name }}</span>
                <div class="tl-cell-sub d-none d-sm-block">{{ metaLine(type) }}</div>
                <!-- X100: a phone's second line (one column, like the All bookings rows) -->
                <div class="d-sm-none ms-type-phone">
                  <span class="ms-type-phone-line">{{ typePhoneLine(type, displayAsLabel(type.displayAs)) }}</span>
                  <membership-status-chip :type="type"/>
                </div>
              </div>
            </div>
          </td>
          <td class="d-none d-sm-table-cell">
            <div class="ms-price">{{ type.priceSummary || (type.isFree ? 'Free' : '') }}</div>
            <div v-if="type.pendingApplicationCount" class="tl-cell-sub">{{ type.pendingApplicationCount }} waiting</div>
          </td>
          <td class="d-none d-sm-table-cell">
            <div class="ms-chips">
              <span class="tl-chip">{{ displayAsLabel(type.displayAs) }}</span>
              <membership-status-chip :type="type"/>
            </div>
          </td>
          <td class="tl-num d-none d-sm-table-cell">
            <div class="ms-count">{{ type.currentCount ?? 0 }}</div>
            <div class="tl-cell-sub">current</div>
          </td>
          <td class="ms-type-actions">
            <div class="tl-rowactions">
              <v-progress-circular v-if="busy[type.id]" size="20" width="2" indeterminate/>
              <slot v-else name="actions" :type="type"/>
            </div>
          </td>
        </tr>
        <tr v-if="types.length === 0">
          <td colspan="5" class="ms-empty"><slot name="empty">Nothing here yet.</slot></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped lang="scss">
.ms-price { font-weight: 600; }

/* the name links to the type in the row's own ink (like .ec-name on the
   competition cards); .tl-cell-link is the grey sub-row helper, not a title */
.ms-type-name {
  color: inherit;
  text-decoration: none;

  &:hover { text-decoration: underline; }
}
.ms-count { font-weight: 800; font-size: 16px; }

/* X100: the types table on a phone is one column (no inner sideways scroll), as the All bookings rows:
   the name, then price · kind · current with the status chip */
.ms-type-text { min-width: 0; }

.ms-type-phone {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 2px;
  min-width: 0;
}

/* X101 (R40 L4): wraps to a second line so "N current · N waiting" always shows */
.ms-type-phone-line {
  flex: 1;
  min-width: 0;
  font-size: 11.5px;
  line-height: 1.45;
  color: rgb(var(--v-theme-textSubtle));
  overflow-wrap: anywhere;
}

@media (max-width: 599.98px) {
  .ms-types-table { min-width: 0; }

  .ms-types-table td { padding: 10px 12px; }

  /* the width-100% / max-width-0 pair: the cell takes the row, and its second line wraps instead of widening it */
  .ms-type-cell { width: 100%; max-width: 0; }

  .ms-type-actions { width: 84px; padding-left: 0 !important; }
}
.ms-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.ms-empty { color: rgb(var(--v-theme-textSubtle)); text-align: center; padding: 28px 16px !important; }
</style>
